import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../AuthProvider';
import { isAdminOnlyDoc } from '../../../utils/docTags';
import { isDocsMetaDoc } from '../../../utils/docOrganization';
import { mentionIconText } from '../utils/mentionUtils';
import { clampFloatingMenuPosition } from '../utils/floatingMenuPosition';
import { usePickerDocs } from '../hooks/usePickerDocs';
import { SnippetSourcePickerOverlay } from './SnippetSourcePickerOverlay';

/**
 * `/snippet` picker:
 *   1) floating doc search
 *   2) full-app read-only source viewer (click / range → Choose)
 */
export const SnippetBlockPicker = ({
  position,
  onPick,
  onClose,
  availableDocs = null,
  excludeDocId = null,
}) => {
  const { role } = useAuth();
  const canViewAdminDocs = (role ?? 'admin') !== 'user';

  const { docs, loadError } = usePickerDocs(availableDocs);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const [sourceDoc, setSourceDoc] = useState(null);
  const menuRef = useRef(null);
  const inputRef = useRef(null);

  const results = useMemo(() => {
    if (!docs) return [];
    const permitted = canViewAdminDocs
      ? docs
      : docs.filter((doc) => !isAdminOnlyDoc(doc));
    const active = permitted.filter(
      (doc) =>
        !doc.deletedAt &&
        !isDocsMetaDoc(doc) &&
        (!excludeDocId || doc.id !== excludeDocId)
    );
    const q = query.trim().toLowerCase();
    if (!q) return active;
    return active.filter(
      (doc) =>
        (doc.title || '').toLowerCase().includes(q) ||
        (doc.tags || []).some((tag) => tag.toLowerCase().includes(q))
    );
  }, [docs, query, canViewAdminDocs, excludeDocId]);

  useEffect(() => setSelectedIndex(0), [query]);

  useEffect(() => {
    if (sourceDoc) return undefined;
    inputRef.current?.focus({ preventScroll: true });
    return undefined;
  }, [sourceDoc]);

  useEffect(() => {
    if (sourceDoc) return undefined;
    const menu = menuRef.current;
    if (!menu) return undefined;
    const apply = () => {
      setAdjustedPosition(clampFloatingMenuPosition(menu, position));
    };
    apply();
    const raf = requestAnimationFrame(apply);
    const ro = new ResizeObserver(apply);
    ro.observe(menu);
    window.addEventListener('resize', apply);
    window.addEventListener('scroll', apply, true);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', apply);
      window.removeEventListener('scroll', apply, true);
    };
  }, [position, results, docs, loadError, sourceDoc]);

  useEffect(() => {
    if (sourceDoc) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onClose, sourceDoc]);

  useEffect(() => {
    if (sourceDoc) return undefined;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, sourceDoc]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) =>
        results.length ? (prev + 1) % results.length : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) =>
        results.length ? (prev - 1 + results.length) % results.length : 0
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[selectedIndex]) setSourceDoc(results[selectedIndex]);
    }
  };

  if (sourceDoc) {
    return (
      <SnippetSourcePickerOverlay
        docMeta={sourceDoc}
        onChoose={(picked) => {
          onPick(picked);
        }}
        onBack={() => setSourceDoc(null)}
        onClose={onClose}
      />
    );
  }

  return createPortal(
    <div
      ref={menuRef}
      className="doc-slash-menu doc-mention-picker snippet-block-picker"
      style={{ top: adjustedPosition.y, left: adjustedPosition.x }}
    >
      <input
        ref={inputRef}
        className="doc-mention-picker__search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search docs to embed from…"
        aria-label="Search docs"
      />
      {docs === null && <div className="doc-slash-empty">Loading docs…</div>}
      {loadError && <div className="doc-slash-empty">{loadError}</div>}
      {docs &&
        !loadError &&
        (results.length === 0 ? (
          <div className="doc-slash-empty">
            {query.trim() ? 'No matches' : 'No docs to embed from'}
          </div>
        ) : (
          results.map((doc, index) => (
            <div
              key={doc.id}
              className={`doc-slash-item ${index === selectedIndex ? 'selected' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                setSourceDoc(doc);
              }}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <span className="doc-slash-icon">{mentionIconText(doc.icon)}</span>
              <span className="doc-slash-label">{doc.title || 'Untitled Doc'}</span>
            </div>
          ))
        ))}
      <div className="doc-mention-picker__hint">
        <span>↑↓ choose</span>
        <span>Enter open</span>
        <span>Esc cancel</span>
      </div>
    </div>,
    document.body
  );
};
